import {
  Button,
  CloseButton,
  Flex,
  Heading,
  IconAddLine,
  List,
  ScreenReaderContent,
  Spinner,
  Tabs,
  Text,
  TextInput,
  Tray,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { ExternalService } from "../../../services/ExternalService";
import { waitForExpectedResult } from "../../../utils/Timer";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import EnrichedFileDrop from "../../LearningOutcomes/FileDrop/EnrichedFileDrop";
import Student from "./Student";
import { sortByLastName } from "./utils";

const RosterManagementTray = ({
  open,
  setOpen,
  fetchData,
  roster,
  setRoster,
  contextId,
  raterData,
  setRaterData,
  user,
  setIsGradingComplete,
  locationState,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [rosterFile, setRosterFile] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [errFlag, setErrFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittingRoster, setSubmittingRoster] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const studentId = useRef();

  const handleAddStudent = () => {
    if (studentId.current.value) {
      setLoading(true);
      ExternalService.addExternalUserToOffering(
        parseInt(studentId.current.value),
        contextId
      ).then((student) => {
        if (student) {
          if (!roster.find((s) => s.ddl_id === student.ddl_id)) {
            setErrFlag("");
            setRoster([...roster, student].sort(sortByLastName));
            if (Object.keys(raterData).length > 0) {
              let first_entry = "";

              const firstId = Object.keys(raterData)[0];
              const outcome_ids = Object.keys(raterData[firstId]);
              first_entry = raterData[firstId];

              raterData[student.canvas_id] = {};

              outcome_ids.forEach((outcome_id) => {
                raterData[student.canvas_id][outcome_id] = {
                  mastery: false,
                  score: null,
                  possible: first_entry[outcome_id].possible,
                  percent: null,
                  submitted_or_assessed_at: "",
                  canvas_user_id: student.canvas_id,
                  canvas_learning_outcome_id:
                    first_entry[outcome_id].canvas_learning_outcome_id,
                  outcome_id: outcome_id,
                  evidence_id: "",
                  purl_assignment_id:
                    first_entry[outcome_id].purl_assignment_id,
                  assessor_id: user,
                  canvas_enrollment_term_id:
                    first_entry[outcome_id].canvas_enrollment_term_id,
                  aggregate_score: null,
                  score_finalized: false,
                  ddl_section_id: contextId,
                };
              });

              setRaterData({ ...raterData });

              window.localStorage.setItem(
                `raterData/${locationState.share_id}`,
                JSON.stringify(raterData)
              );
            }
            setIsGradingComplete(false);
            setLoading(false);
            setRefreshData(true);
            studentId.current.value = "";
          } else {
            setErrFlag(t("rater.studentAlreadyInRoster"));
            setLoading(false);
          }
        } else {
          setErrFlag(t("rater.studentCanvasIdNotFound"));
          setLoading(false);
        }
      });
    } else {
      setErrFlag(t("rater.canvasIdRequired"));
    }
  };

  const handleSubmitFile = () => {
    setSubmittingRoster(true);
    ExternalService.saveFile(rosterFile, "roster")
      .then(() => {
        waitForExpectedResult(
          () => ExternalService.getExternalRosterByContext(contextId),
          (newRoster) => {
            return roster.length !== newRoster.length;
          },
          () => {
            // give the roster a chance to propogate to Neptune
            setOpen(false);
            setSubmittingRoster(false);
            showNotification(t("juriedAssessment.uploadRosterSuccess"));
            fetchData();
          },
          () => {
            setSubmittingRoster(false);
            showNotification(t("juriedAssessment.uploadRosterFail"), ERROR);
          },
          20,
          1000
        );
      })
      .catch(() => {
        setSubmittingRoster(false);
        showNotification(t("juriedAssessment.uploadRosterFail"), ERROR);
      });
  };

  const handleCancel = () => {
    setOpen(false);
    if (refreshData) {
      fetchData();
      setRefreshData(false);
    }
  };

  return (
    <>
      <Tray
        size="regular"
        label={t("rater.rosterManagement")}
        open={open}
        placement="end"
      >
        <View as="div" padding="small">
          <Heading level="h2" as="h1" border="bottom" margin="0 0 small 0">
            {t("rater.rosterManagement")}
          </Heading>
          <CloseButton
            placement="end"
            offset="small"
            screenReaderLabel={t("global.close")}
            onClick={handleCancel}
          />
          <Tabs onRequestTabChange={(_e, { index }) => setSelectedIndex(index)}>
            <Tabs.Panel
              renderTitle="Roster View"
              isSelected={selectedIndex === 0}
            >
              <Flex alignItems="center" gap="small" margin="0 0 small 0">
                <Flex.Item shouldGrow>
                  <TextInput
                    type="text"
                    size="small"
                    placeholder={t("rater.canvasId")}
                    renderLabel={
                      <ScreenReaderContent>
                        {t("rater.addStudent")}
                      </ScreenReaderContent>
                    }
                    messages={errFlag ? [{ text: errFlag, type: "error" }] : []}
                    inputRef={(el) => (studentId.current = el)}
                  />
                </Flex.Item>
                <Flex.Item align="start">
                  <Button
                    size="small"
                    renderIcon={
                      loading ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconAddLine
                      )
                    }
                    disabled={loading}
                    onClick={handleAddStudent}
                  >
                    {t("rater.addStudent")}
                  </Button>
                </Flex.Item>
              </Flex>
              <List isUnstyled>
                {roster.length > 0 ? (
                  roster.map((student, index) => (
                    <List.Item id={index} key={index} margin="small 0">
                      <Student
                        student={student}
                        setRoster={setRoster}
                        raterData={raterData}
                        setRaterData={setRaterData}
                        contextId={contextId}
                        shareId={locationState.share_id}
                        assessmentId={locationState.assessment_id}
                        setRefreshData={setRefreshData}
                      />
                    </List.Item>
                  ))
                ) : (
                  <List.Item id="no-students">
                    <Text>{t("rater.noStudents")}</Text>
                  </List.Item>
                )}
              </List>
            </Tabs.Panel>
            <Tabs.Panel
              renderTitle={t("rater.rosterUpload")}
              isSelected={selectedIndex === 1}
            >
              <View as="div">
                <EnrichedFileDrop
                  fileState={rosterFile}
                  setFileState={setRosterFile}
                  enriched_content={{ context_id: contextId }}
                  fileDropHeading={t("rater.rosterUpload")}
                  fileExtension=".csv"
                />
                <div style={{ marginTop: 10 }}>
                  <Text as="div">
                    <Link to={"/sampleStudents.csv"} target="_blank">
                      {t("global.downloadExampleFile")}
                    </Link>
                  </Text>
                  <Text>{t("rater.rosterRequiredFields")}</Text>
                </div>
                <Flex justifyItems="end" gap="small" padding="small 0 0 0">
                  <Flex.Item>
                    <Button onClick={handleCancel}>{t("global.cancel")}</Button>
                  </Flex.Item>
                  <Flex.Item>
                    <Button
                      onClick={() => {
                        if (rosterFile) {
                          handleSubmitFile();
                        }
                      }}
                      color="primary"
                      disabled={submittingRoster}
                    >
                      {submittingRoster ? (
                        <Spinner
                          size="x-small"
                          renderTitle={t("global.pleaseWait")}
                        />
                      ) : (
                        t("global.submit")
                      )}
                    </Button>
                  </Flex.Item>
                </Flex>
              </View>
            </Tabs.Panel>
          </Tabs>
        </View>
      </Tray>
    </>
  );
};

export default RosterManagementTray;
